import React from 'react';
import Box from '@mui/material/Box';
import { useStacklineTheme } from '@stackline/ui';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import ExportDownloadTable from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/ExportDownloadTable';
import { BEACON_PRO_PAGE_MARGIN_BOTTOM } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { getQueryParamValue, shouldShowPresentations, shouldShowDigitalShelfTab } from 'src/utils/app';
import AtlasV2ShelfDownloadTable from 'src/components/AtlasV2Components/AtlasV2Settings/AtlasV2ShelfDownloadTable';
import AtlasV2PresentationsDownloadTable from 'src/components/AtlasV2Components/AtlasV2Settings/AtlasV2PresentationsDownloadTable';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import { NotificationBadge } from 'src/components/common/NotificationBadge';

export enum DownloadsTab {
  ExportsDownload = 'exportsDownload',
  PresentationsDownload = 'presentationsDownload',
  ShelfDownload = 'shelfDownload'
}

interface DownloadTabsProps {
  tab: DownloadsTab;
}

const isPresentationsPage = () => {
  return getQueryParamValue('tab') === 'presentationsDownload';
};

const isShelfPage = () => {
  return getQueryParamValue('tab') === 'shelfDownload';
};

const AtlasV2DownloadsTabs = ({ tab }: DownloadTabsProps) => {
  const { beaconNavigator } = useBeaconNavigator();
  const { notificationBadges } = useNotificationsBadge();

  return (
    <GenericNavigationTabs
      tab={tab}
      tabs={[
        {
          label: (
            <div>
              General
              {notificationBadges.export && (
                <NotificationBadge variant="small" />
              )}
            </div>
          ),
          onClick: () => beaconNavigator.updateSearchParams({ tab: DownloadsTab.ExportsDownload }).go(),
          value: DownloadsTab.ExportsDownload
        },
        ...(shouldShowPresentations() ? [
          {
            label: (
              <div>
                Presentations
                {notificationBadges.presentations && (
                  <NotificationBadge variant="small" />
                )}
              </div>
            ),
            onClick: () => beaconNavigator.updateSearchParams({ tab: DownloadsTab.PresentationsDownload }).go(),
            value: DownloadsTab.PresentationsDownload
          }
        ] : []),
        ...(shouldShowDigitalShelfTab() ? [
          {
            label: 'Shelf',
            onClick: () => beaconNavigator.updateSearchParams({ tab: DownloadsTab.ShelfDownload }).go(),
            value: DownloadsTab.ShelfDownload
          }
        ] : [])
      ]}
    />
  );
};

const AtlasV2DownloadsPageLayout = () => {
  const theme = useStacklineTheme();

  return (
    <Box width="100%">
      <Text variant="h2">Download List</Text>
      <AtlasV2DownloadsTabs tab={getQueryParamValue('tab') as DownloadsTab} />
      <Box marginTop={theme.spacing.lg} marginBottom={BEACON_PRO_PAGE_MARGIN_BOTTOM}>
        {isPresentationsPage() ? (
          <AtlasV2PresentationsDownloadTable />
        ) : isShelfPage() && shouldShowDigitalShelfTab() ? (
          <AtlasV2ShelfDownloadTable />
        ) : (
          <ExportDownloadTable />
        )}
      </Box>
    </Box>
  );
};

export default AtlasV2DownloadsPageLayout;
